<template>
  <div class="w-full mx-auto">
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xl">
        Rejection thread <span class="text-zuno-turquoise">({{ messages.length }}) <br>    <span class="text-gray-800 text-sm">Submission ID: {{ ticketId }}</span>
        </span>
      </h2>
      <div class="flex items-center gap-2">
        <span class="text-sm">{{ sortOrder === 'desc' ? 'Newest' : 'Oldest' }} first</span>
        <button
          class="p-1 rounded hover:bg-gray-100"
          @click="handleSort"
        >
          <SortIcon class="w-4 h-4" />
        </button>
        <button
          v-if="isModalMode"
          class="flex h-8 w-8 items-center justify-center rounded-md bg-[#EEEFF0D4] text-[#808080] outline-none"
          @click="$emit('close-modal')"
        >
          <TimesSmall />
        </button>
      </div>
    </div>

    <div
      v-if="error"
      class="py-8 text-center text-red-500"
    >
      {{ error }}
    </div>

    <div
      v-else
      class="space-y-6 max-h-[450px] p-3 overflow-y-auto"
    >
      <div
        v-for="message in messages"
        :key="message.id"
        class="flex flex-col message-content"
        :class="{ 'opacity-50': message.resolved }"
      >
        <div class="flex gap-3">
          <div class="relative">
            <div class="h-8 w-8 rounded-full bg-main-primary text-white flex items-center justify-center text-sm no-localize-translate">
              {{ message.avatar }}
            </div>
            <div
              v-if="message.threads?.length"
              class="absolute left-4 top-8 h-full w-[2px] bg-gray-200"
              :style="{
                height: `${getLineHeight(message.id)}px`,
                transform: 'translateX(-50%)',
              }"
            />
          </div>

          <div class="flex-1">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-900 no-localize-translate">{{ message.author }}</span>
                <span class="text-sm text-gray-500">•</span>
                <span class="text-sm text-gray-500">{{ formatDate(message.created_at) }}</span>
              </div>

              <div class="flex items-center justify-end gap-3">
                <button
                  v-if="isCurrentUserMessage(message)"
                  class="flex items-center gap-2 px-2 py-1 text-sm text-gray-500 rounded hover:bg-gray-50"
                  @click="handleResolve(message, message.resolved)"
                >
                  <div
                    class="relative inline-flex h-2.5 w-5 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out items-center"
                    :class="message.resolved ? 'bg-green-600' : 'bg-gray-200'"
                  >
                    <span
                      class="inline-block w-2 h-2 transition duration-200 ease-in-out transform bg-white rounded-full"
                      :class="message.resolved ? 'translate-x-2 ml-0.5' : 'translate-x-0.5'"
                    />
                  </div>
                  <span class="text-xs">{{ message.resolved ? 'Resolved' : 'Resolve' }}</span>
                </button>

                <div
                  v-if="isCurrentUserMessage(message) && !message.resolved"
                  class="relative"
                >
                  <button
                    class="rounded hover:bg-gray-200"
                    @click="toggleDropdown(message.id)"
                  >
                    <MoreHorizontal class="w-4 h-4" />
                  </button>

                  <div
                    v-if="openDropdownId === message.id"
                    class="absolute right-0 z-20 w-20 mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <button
                      v-if="!editingMessageId"
                      class="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                      @click="startEditing(message)"
                    >
                      Edit
                    </button>
                    <button
                      v-else
                      class="block w-full px-4 py-2 text-sm text-left text-white bg-main-primary"
                      @click="cancelEdit"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-1">
              <div v-if="editingMessageId === message.id">
                <div class="space-y-4">
                  <TipTapInput
                    ref="tiptapEditor"
                    :value="message.comment"
                    :mentionable-users="mentionableUsers"
                    :is-modal-mode="false"
                    @save-button-clicked="updateEditContent($event, message)"
                    @selected-ids="handleSelectedUsers"
                    @cancel-button-click="cancelEdit"
                  />
                </div>
              </div>
              <CommentText
                v-else
                :text="message.comment"
              />
            </div>

            <button
              v-if="!isReplyingTo(message.id) && editingMessageId !== message.id && !message.resolved"
              class="text-[#218579] text-sm hover:underline mt-2"
              @click="startReply(message.id)"
            >
              Reply
            </button>

            <!-- TipTap Reply Input -->
            <div
              v-if="isReplyingTo(message.id)"
              class="mt-4"
            >
              <TipTapInput
                :auto-focus="true"
                :mentionable-users="mentionableUsers"
                @save-button-clicked="handleReplySave($event, message)"
                @cancel-button-click="handleReplyCancel"
                @selected-ids="handleSelectedUsers"
              />
            </div>
          </div>
        </div>
        <div
          v-if="message.threads?.length"
          :ref="el => { if (el) repliesRefs[message.id] = el }"
          class="mt-4 space-y-4 ml-11"
          :class="{ 'pointer-events-none': message.resolved }"
        >
          <div
            v-for="reply in sortedReplies(message.threads)"
            :key="reply.id"
            class="flex gap-3"
          >
            <div
              class="h-8 w-8 rounded-full bg-main-primary text-white flex items-center justify-center text-sm relative no-localize-translate"
            >
              {{ reply.avatar }}
            </div>

            <div class="flex-1">
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-900 no-localize-translate">{{ reply.author }}</span>
                  <span
                    v-if="reply.user_id === currentUser.id"
                    class="text-sm text-gray-500"
                  >(You)</span>
                  <span class="text-sm text-gray-400">•</span>
                  <span class="text-sm text-gray-400">{{ formatDate(reply.created_at) }}</span>
                </div>

                <div
                  v-if="isCurrentUserMessage(reply) && !message.resolved"
                  class="relative no-localize-translate"
                >
                  <button
                    class="rounded hover:bg-gray-200"
                    @click="toggleDropdown(reply.id)"
                  >
                    <MoreHorizontal class="w-4 h-4" />
                  </button>

                  <div
                    v-if="openDropdownId === reply.id"
                    class="absolute right-0 z-20 w-20 mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <button
                      v-if="!editingMessageId"
                      class="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                      @click="startEditing(reply)"
                    >
                      Edit
                    </button>
                    <button
                      v-else
                      class="block w-full px-4 py-2 text-sm text-left text-white bg-main-primary"
                      @click="cancelEdit"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              <div class="mt-1 text-sm">
                <div v-if="editingMessageId === reply.id">
                  <div class="space-y-4">
                    <TipTapInput
                      ref="tiptapEditorReply"
                      :value="reply.comment"
                      :mentionable-users="mentionableUsers"
                      :is-modal-mode="false"
                      @save-button-clicked="updateEditContent($event, reply)"
                      @selected-ids="handleSelectedUsers"
                      @cancel-button-click="cancelEdit"
                    />
                  </div>
                </div>
                <CommentText
                  v-else
                  :text="reply.comment"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MoreHorizontal } from 'lucide-vue-next'

export default {
  name: 'RejectionMessageThread',

  components: {
    MoreHorizontal,
  },

  props: {
    isModalMode: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
    comments: {
      type: Array,
      default: () => [],
    },
    ticketId: {
      type: Number,
      default: 0,
    },
  },

  emits: ['close-modal', 'update-message'],

  data() {
    return {
      messages: this.comments,
      sortOrder: 'desc',
      newMessage: '',
      openDropdownId: null,
      loading: false,
      error: null,
      useDummyMessages: null,
      editingMessageId: null,
      editedContent: null,
      repliesRefs: {},
      lineHeights: {},
      activeReplyId: null,
      selectedUserIds: [],
      mentionableUsers: this.users,
    }
  },

  computed: {
    currentUser() {
      return this.$store.auth.authenticatedUser
    },
    currentUserFullName() {
      const { first_name = '', last_name = '' } = this.currentUser
      return `${first_name} ${last_name}`.trim()
    },
  },

  watch: {
    comments(value) {
      this.messages = value
    },
    users(newValue) {
      this.mentionableUsers = newValue
    },
    messages: {
      handler() {
        this.updateLineHeights()
      },
      deep: true,
    },
  },

  methods: {
    isCurrentUserMessage(message) {
      const result = message.user_id === this.currentUser.id
      return result
    },

    handleSort() {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      this.messages.sort((a, b) => {
        const comparison = new Date(a.created_at) - new Date(b.created_at)
        return this.sortOrder === 'desc' ? -comparison : comparison
      })
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    },

    toggleDropdown(messageId) {
      this.openDropdownId = this.openDropdownId === messageId ? null : messageId
    },

    getHighlightedText(inputText) {
      const regex = /@([A-Za-z]+ [A-Za-z]+)/g
      return inputText.replace(regex, (match, group1) => {
        return `<span class="bg-[#E9F3F2] text-main-primary px-1.5 py-0.5 rounded">@${group1}</span>`
      })
    },
    startEditing(message) {
      this.editingMessageId = message.id
      this.editedContent = message.comment
      this.openDropdownId = null
    },

    updateEditContent(content, editMessage) {
      this.editedContent = content
      this.$apis.companies.editComment({ company: editMessage.company, comment_id: editMessage.id, comment: content }).then(() => {
        this.messages = []
        this.editingMessageId = null
        this.editedContent = null
        this.selectedUserIds = []
        this.loadComments(editMessage.company, editMessage.submission)
      })
    },

    cancelEdit() {
      this.editingMessageId = null
      this.editedContent = null
      this.openDropdownId = null
      this.selectedUserIds = []
    },

    // custom line heights/stepper
    getLineHeight(messageId) {
      const repliesEl = this.repliesRefs[messageId]
      if (!repliesEl) return 0

      // check if replying to main message or any of its replies
      const isReplyingToMain = this.isReplyingTo(messageId)

      // add safe checks for threads
      const message = this.messages.find(m => m.id === messageId)
      const isReplyingToAnyReply = message?.threads?.some(reply =>
        this.isReplyingTo(`${messageId}-${reply.id}`),
      ) || false

      const extraPadding = (isReplyingToMain || isReplyingToAnyReply) ? 150 : 50
      return repliesEl.offsetHeight + extraPadding
    },

    updateLineHeights() {
      this.$nextTick(() => {
        Object.keys(this.repliesRefs).forEach((messageId) => {
          this.lineHeights[messageId] = this.getLineHeight(messageId)
        })
      })
    },

    // reply functions
    isReplyingTo(id) {
      return this.activeReplyId === id
    },

    startReply(id) {
      this.activeReplyId = id
    },

    sortedReplies(threads) {
      if (!threads) return []
      return [...threads].reverse()
    },

    async handleReplySave(content, selectedMessage) {
      try {
        const parsedUrl = new URL(window.location.href)
        const baseUrl = `${parsedUrl.protocol}//${parsedUrl.host}/dashboard`

        // parent id from replies
        let parentId = selectedMessage.id
        let replyChain = []

        // reply chain to ensure proper nesting
        const findReplyChain = (messages, targetId) => {
          for (const msg of messages) {
            if (msg.id === targetId) {
              return [msg]
            }
            if (msg.threads) {
              for (const reply of msg.threads) {
                if (reply.id === targetId) {
                  return [msg, reply]
                }
              }
            }
          }
          return null
        }

        replyChain = findReplyChain(this.messages, this.activeReplyId) || [selectedMessage]
        parentId = replyChain[replyChain.length - 1].id

        const approver_link = `${baseUrl}/ghg-approval?showThread=${selectedMessage.submission}`
        const data_owner_link = `${baseUrl}/data-upload/submissions?showThread=${selectedMessage.submission}`

        const payload = {
          company: selectedMessage.company,
          comment_id: parentId,
          comment: content,
          submission: selectedMessage.submission,
          recipients: this.selectedUserIds.join(','), // Comma-separated list of user IDs
          data_owner_link, // Full URL for data owners
          approver_link, // Full URL for approvers
        }

        await this.$apis.companies.saveReplyComment(payload)
        this.messages = []
        this.selectedUserIds = []
        this.loadComments(selectedMessage.company, selectedMessage.submission)
      }
      catch (error) {
        console.error('Error saving reply:', error)
        this.$toast?.error('Failed to save reply')
      }
      finally {
        // reset states
        this.activeReplyId = null
        this.selectedUserIds = []
        // update line heights after adding reply
        this.updateLineHeights()
      }
    },

    handleReplyCancel() {
      this.activeReplyId = null
    },

    handleSelectedUsers(userIds) {
      this.selectedUserIds = userIds
    },

    // resolve functions
    handleResolve(message, resolveStatus) {
      this.$apis.companies.resolveComment(message.company, message.id, !resolveStatus).then(() => {
        this.messages = []
        this.selectedUserIds = []
        this.loadComments(message.company, message.submission)
      })
    },
    loadComments(cid, submissionId) {
      this.$apis.companies.comments(cid, submissionId).then(({ data }) => {
        this.messages = data.map((comment) => {
          comment.author = `${comment.created_by?.first_name} ${comment.created_by.last_name}`
          comment.avatar = comment.created_by?.first_name.charAt(0) + comment.created_by?.last_name.charAt(0)
          comment.user_id = comment.created_by?.id
          comment.threads = comment.replies.length
            ? comment.replies.map((reply) => {
              reply.author = `${reply.created_by?.first_name} ${reply.created_by.last_name}`
              reply.avatar = reply.created_by?.first_name.charAt(0) + reply.created_by?.last_name.charAt(0)
              reply.user_id = reply.created_by?.id
              reply.threads = reply.replies.length
                ? reply.replies.map((user) => {
                  user.author = `${user.created_by?.first_name} ${user.created_by.last_name}`
                  user.user_id = user.created_by?.id
                  user.avatar = user.created_by?.first_name.charAt(0) + user.created_by?.last_name.charAt(0)
                  user.threads = user.replies.length ? user.replies : []
                  return user
                })
                : []
              return reply
            })
            : []
          return comment
        })
      })
    },
  },
}
</script>

<style scoped>
:deep(.message-content) ul {
  list-style-type: disc !important;
  padding-left: 1.5rem !important;
}

:deep(.message-content) li {
  margin: 0.25rem 0 !important;
}
</style>
